import { Groups2Outlined } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { type QueryStatus } from "@tanstack/react-query";

import { PhoneAndEmailRenderer } from "~ui/atoms/DataGrid";
import { DataGrid, type DataGridProps } from "~ui/molecules/DataGrid/DataGrid";
import { getDate, getTime } from "~utils/date";

const tActiveVisitors = "Active Visitors";
const tActiveVisitorsSubtitle = "People currently signed in at this school.";
const tName = "Name";
const tContact = "Contact";
const tSignedIn = "Signed in";
const tNoActiveVisitors = "No one is signed in right now.";
const tActiveVisitorsLoadError = "Could not load active visitors.";

export interface VisitorManagementActiveVisitorRow {
  contactEmail?: string | null;
  contactPhone?: string | null;
  name: string;
  rowId: string;
  signedInAtMs: number;
}

function formatSignedInAt(timestamp?: string | null): string {
  if (!timestamp) {
    return "—";
  }
  return `${getDate(timestamp, "MMM d, yyyy")} ${getTime(timestamp)}`;
}

const activeVisitorColumns: DataGridProps<VisitorManagementActiveVisitorRow>["columns"] = [
  { field: "name", filterable: false, flex: 1, headerName: tName, minWidth: 160 },
  {
    field: "contactEmail",
    filterable: false,
    flex: 1,
    headerName: tContact,
    minWidth: 220,
    renderCell: ({ row }) => <PhoneAndEmailRenderer email={row.contactEmail} phoneNumber={row.contactPhone} />,
    valueGetter: ({ row }) => [row.contactEmail, row.contactPhone].filter(Boolean).join(" ") || "",
  },
  {
    field: "signedInAtMs",
    filterable: false,
    headerName: tSignedIn,
    minWidth: 180,
    renderCell: ({ row }) => (
      <Typography variant="body2">
        {row.signedInAtMs ? formatSignedInAt(new Date(row.signedInAtMs).toISOString()) : formatSignedInAt(null)}
      </Typography>
    ),
    sortComparator: (v1, v2) => (v1 as number) - (v2 as number),
    width: 220,
  },
];

export interface VisitorManagementActiveVisitorsSectionProps {
  rows: VisitorManagementActiveVisitorRow[];
  status: QueryStatus;
}

export function VisitorManagementActiveVisitorsSection({ rows, status }: VisitorManagementActiveVisitorsSectionProps) {
  return (
    <>
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          gap: { sm: 2, xs: 1 },
          mb: 0.5,
        }}
      >
        <Groups2Outlined data-variant="page-header" />
        <Typography component="h2" variant="page-header">
          {tActiveVisitors}
        </Typography>
      </Box>
      <Typography color="text.secondary" component="p" sx={{ mb: 2 }} variant="subtitle1">
        {tActiveVisitorsSubtitle}
      </Typography>
      <Box sx={{ overflow: "auto" }}>
        <DataGrid<VisitorManagementActiveVisitorRow>
          disableColumnMenu
          columns={activeVisitorColumns}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
            sorting: { sortModel: [{ field: "signedInAtMs", sort: "desc" }] },
          }}
          pageSizeOptions={[5, 10, 25]}
          rows={rows}
          status={status}
          toolbarCustomProps={{ widgetOptions: { show: false, showPrintWidget: false } }}
          slotProps={{
            empty: { message: tNoActiveVisitors },
            error: { message: tActiveVisitorsLoadError },
            toolbar: { showQuickFilter: false },
          }}
          sx={{
            "& .MuiDataGrid-cell": { py: 0.75 },
            minHeight: 320,
          }}
        />
      </Box>
    </>
  );
}
